import React, { useState, useMemo, useEffect } from "react";
import {
  Button,
  Divider,
  Form,
  Input,
  Row,
  Col,
  Radio,
  Collapse,
  message,
  Spin,
  Tag,
  Checkbox,
} from "antd";
import Loading from "../Common/Loading";
import { convertProviderApiToFormValues } from "../../Helpers/ServiceProviderHelpers";
import {
  colLayout,
  rowLayout,
  tailItemLayout,
  ServiceProviderActions,
  ServiceProviderTypes,
} from "../../Constants/Constants";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { onFormSubmit } from "../../ApiHandlers/ServiceProviderApiHandlers";
import { apiCallback } from "../../ApiHandlers/Common";
import { DiscardWarning } from "../Common/DiscardWarning";
import { BreadCrumbs } from "../Common/BreadCrumbs";
import { jsonValidator } from "../../Constants/Utilities";

const DefaultAccordianTab = ["admin_contact"];

const getInitialValues = (record) => {
  const initialValues = {
    type: ServiceProviderTypes.tenant.value,
    storeUserdata: false,
  };
  if (record) {
    initialValues.name = record.name;
    initialValues.type = record.type;
    initialValues.status = record.status;
    initialValues.providerCode = record.providerCode;
    initialValues.location = record.locationinfo?.name;
  }
  return initialValues;
};

const getTitle = (action) => {
  switch (action) {
    case ServiceProviderActions.add:
      return "Add";
    case ServiceProviderActions.edit:
      return "Update";
    case ServiceProviderActions.view:
      return "View";
    default:
      return "";
  }
};

const ServiceProviderForm = (props) => {
  console.log("ServiceProviderForm props", props);
  const { action, history, record, onActionClick, serviceProviderApi } = props;

  const isAddForm = props.action === ServiceProviderActions.add;

  const isViewForm = action === ServiceProviderActions.view;

  const title = useMemo(() => getTitle(action), [action]);
  const [initialValues, setInitialValues] = useState(getInitialValues(record));

  // service info
  const [fetchingProviderDetails, setfetchingProviderDetails] = useState(false);

  // provider info
  const [processing, setProcessing] = useState(false);

  const [form] = Form.useForm();

  useEffect(() => {
    record && fetchProviderDetails();
  }, []);

  const fetchProviderDetails = () => {
    if (!record || !record.providerId) return;
    const providerId = record.providerId;

    setfetchingProviderDetails(true);
    const errorCallback = (error) => {
      setfetchingProviderDetails(false);
      message.error("Error fetching provider list");
      console.log("fetchRecordDetails", error);
    };
    const successCallback = (data) => {
      console.log("fetchRecordDetails data", data);
      const initialValues = convertProviderApiToFormValues(data);
      initialValues.website = data.serviceProvider.website || '';
      setInitialValues(initialValues);
      setfetchingProviderDetails(false);
    };
    try {
      serviceProviderApi.getServiceproviderById(
        providerId,
        apiCallback({ history, errorCallback, successCallback })
      );
    } catch (error) {
      errorCallback(error);
    }
  };

  const phoneNumberValidator = async (_, value) => {
    if (!!!value) {
      return;
    }
    const regex = /[-+\(\)0-9]/;
    if (!value.match(regex)) {
      return Promise.reject(new Error("Only numbers allowed"));
    }
    if (value.length < 10) {
      return Promise.reject(new Error("Please enter valid phone number"));
    }
    if (value.length > 18) {
      return Promise.reject(
        new Error("Max 18 chars allowed (including digits, country code and special characters)")
      );
    }
  };

  const onBackClick = () => {
    const isChanged =
      action === ServiceProviderActions.view ? false : checkForInputChanges();
    DiscardWarning({ isChanged, onActionClick })();
  };

  const checkForInputChanges = () => {
    const fieldsToCheck = [
      "commDesc",
      "commEmail",
      "commName",
      "techEmail",
      "techName",
      "adminEmail",
      "adminName",
      "description",
      "type",
      "providerCode",
      "dispName",
      "name",
      "website",
    ];
    const values = form.getFieldsValue();
    let isInputsChanged = false;
    fieldsToCheck.every((f) => {
      if (
        values.hasOwnProperty(f) &&
        values[f] !== initialValues[f] &&
        (!!values[f] || !!initialValues[f])
      ) {
        isInputsChanged = true;
        return false;
      }
      return true;
    });
    return isInputsChanged;
  };

  return (
    <div className="list-container">
      <div className="page-header-container">
        <BreadCrumbs
          headers={props.parentBread.concat(`${title} Service Provider`)}
          onParentActionClick={onBackClick}
        />
      </div>
      <Divider className="divider" />
      <div className="form-data-container">
        {fetchingProviderDetails ? (
          <Loading height="300px" message="Fetching details..." />
        ) : (
          <fieldset disabled={isViewForm}>
            <Form
              form={form}
              name="provider-form"
              layout="vertical"
              requiredMark={"optional"}
              initialValues={initialValues}
              onFinish={onFormSubmit({
                props,
                setProcessing,
                setInitialValues,
              })}
            >
              <Spin spinning={processing} tip={"Processing, please wait..."}>
                <Row {...rowLayout}>
                  <Col {...colLayout}>
                    <Form.Item
                      name="name"
                      label="Name:"
                      rules={[
                        {
                          required: true,
                          message: "Please fill in this field",
                        },
                      ]}
                    >
                      <Input
                        title="Name should be unique"
                        disabled={props.action !== ServiceProviderActions.add}
                      />
                    </Form.Item>
                  </Col>
                  <Col {...colLayout}>
                    <Form.Item name="dispName" label="Display Name:">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
                <Row {...rowLayout}>
                  <Col {...colLayout}>
                    <Row {...rowLayout} style={{ marginLeft: "0px", marginRight: "0px", gap: "15px"}}>                      
                      <Form.Item
                        name="providerCode"
                        label="Provider Code:"
                        rules={[
                          {
                            required: true,
                            message: "Please fill in this field",
                          },
                        ]}
                      >
                        <Input
                          maxLength={3}
                          style={{ maxWidth: "40px" }}
                          disabled={!isAddForm && !!initialValues.providerCode}
                          onInput={(e) =>
                            (e.target.value = ("" + e.target.value).toUpperCase())
                          }
                        />
                      </Form.Item>
                      <Form.Item
                      style={{ flex: "auto", maxWidth: "100%"}}
                        name="website"
                        label="Support URL:"                      
                      >
                        <Input style={{ width: "99%"}}/>
                      </Form.Item>
                    </Row>
                  </Col>
                  <Col {...colLayout}>
                    <Form.Item
                      name="type"
                      label="Type:"
                      rules={[{ required: true }]}
                    >
                      {props.action === ServiceProviderActions.view ? (
                        <Tag>{initialValues.type}</Tag>
                      ) : (
                        <Radio.Group
                          options={Object.values(ServiceProviderTypes)}
                          value={initialValues.type}
                          optionType="button"
                        />
                      )}
                    </Form.Item>
                  </Col>
                </Row>
                <Row {...rowLayout}>
                  <Col {...colLayout}>
                    <Form.Item name="description" label="Description:">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col {...colLayout}>
                    <Form.Item name="location" label="Location:">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <Collapse accordion defaultActiveKey={DefaultAccordianTab}>
                  <Collapse.Panel header="Admin Contact" key="admin_contact">
                    <Row {...rowLayout}>
                      <Col {...colLayout}>
                        <Form.Item name="adminName" label="Name:">
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col {...colLayout}>
                        <Form.Item
                          hasFeedback
                          name="adminPhone"
                          label="Phone:"
                          validateTrigger={["onChange", "onBlur"]}
                          rules={[{ validator: phoneNumberValidator }]}
                        >
                          <Input type="text" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row {...rowLayout}>
                      <Col
                        {...colLayout}
                        className="provider-contact-email-column"
                      >
                        <Form.Item name="adminEmail" label="Email:">
                          <Input type="email" />
                        </Form.Item>
                        <label className="input-placeholder">
                          e.g. abc@mno.xyz
                        </label>
                      </Col>
                      <Col {...colLayout}>
                        <Form.Item name="adminDesc" label="Description:">
                          <Input.TextArea
                            autoSize={{ minRows: 2, maxRows: 3 }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Collapse.Panel>
                  <Collapse.Panel header="Technical Contact" key="tech_contact">
                    <Row {...rowLayout}>
                      <Col {...colLayout}>
                        <Form.Item name="techName" label="Name:">
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col {...colLayout}>
                        <Form.Item
                          hasFeedback
                          name="techPhone"
                          label="Phone:"
                          validateTrigger={["onChange", "onBlur"]}
                          rules={[{ validator: phoneNumberValidator }]}
                        >
                          <Input type="text" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row {...rowLayout}>
                      <Col
                        {...colLayout}
                        className="provider-contact-email-column"
                      >
                        <Form.Item name="techEmail" label="Email:">
                          <Input type="email" />
                        </Form.Item>
                        <label className="input-placeholder">
                          e.g. abc@mno.xyz
                        </label>
                      </Col>
                      <Col {...colLayout}>
                        <Form.Item name="techDesc" label="Description:">
                          <Input.TextArea
                            autoSize={{ minRows: 2, maxRows: 3 }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Collapse.Panel>
                  <Collapse.Panel
                    header="Commercial Contact"
                    key="comm_contact"
                  >
                    <Row {...rowLayout}>
                      <Col {...colLayout}>
                        <Form.Item name="commName" label="Name:">
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col {...colLayout}>
                        <Form.Item
                          hasFeedback
                          name="commPhone"
                          label="Phone:"
                          validateTrigger={["onChange", "onBlur"]}
                          rules={[{ validator: phoneNumberValidator }]}
                        >
                          <Input type="text" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row {...rowLayout}>
                      <Col
                        {...colLayout}
                        className="provider-contact-email-column"
                      >
                        <Form.Item name="commEmail" label="Email:">
                          <Input type="email" />
                        </Form.Item>
                        <label className="input-placeholder">
                          e.g. abc@mno.xyz
                        </label>
                      </Col>
                      <Col {...colLayout}>
                        <Form.Item name="commDesc" label="Description:">
                          <Input.TextArea
                            autoSize={{ minRows: 2, maxRows: 3 }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Collapse.Panel>
                  <Collapse.Panel header="API Info" key="api_info">
                    <Row {...rowLayout}>
                      <Col {...colLayout} style={{ display: "flex" }}>
                        <Form.Item
                          name="storeUserdata"
                          valuePropName="checked"
                          label="Store Userdata:"
                          rules={[{ required: true }]}
                          className="ant-form-row-layout"
                        >
                          <Checkbox />
                        </Form.Item>
                      </Col>
                      <Col {...colLayout}>
                        <Form.Item name="apiEndpoint" label="Endpoint:">
                          <Input />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row {...rowLayout}>
                      <Col {...colLayout}>
                        <Form.Item name="apiUsername" label="Username:">
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col {...colLayout}>
                        <Form.Item name="apiPassword" label="Password:">
                          <Input />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row {...rowLayout}>
                      <Col {...colLayout}>
                        <Form.Item name="apiAuthtype" label="Auth Type:">
                          <Input />
                        </Form.Item>
                        <Form.Item name="apiClientId" label="Client ID:">
                          <Input />
                        </Form.Item>
                        <Form.Item
                          name="apiClientSecret"
                          label="Client Secret:"
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col {...colLayout}>
                        <Form.Item
                          name="apiPlugin"
                          label="Plugin:"
                          rules={[{ validator: jsonValidator() }]}
                        >
                          <Input.TextArea
                            autoSize={{ minRows: 8, maxRows: 8 }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Collapse.Panel>
                </Collapse>

                {!isViewForm && (
                  <Form.Item {...tailItemLayout}>
                    <Button
                      type="primary"
                      htmlType="submit"
                      className="submit-button"
                    >
                      {title}
                    </Button>
                    <Button
                      htmlType="button"
                      onClick={() => form.resetFields()}
                      className="cancel-button"
                    >
                      Cancel
                    </Button>
                  </Form.Item>
                )}
              </Spin>
            </Form>
          </fieldset>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  serviceProviderApi: state.apis.serviceProviderApi,
  serviceApi: state.apis.serviceApi,
});

const connectedServiceProviderForm =
  connect(mapStateToProps)(ServiceProviderForm);

export default withRouter(connectedServiceProviderForm);
